import { jobGuidManagerFactory } from '@shipstation/async-client-manager';
import { of, EMPTY } from 'rxjs';
import { mergeMap, catchError, take } from 'rxjs/operators';

import { NotificationService } from 'ui-components/services/notification-service';

import { LogService } from '../../../services/log-service';
import { BULK_PRINT } from '../../../services/pusher-service/pusher-contract-messages';
import { BULK_PRINT_TIMEOUT_MS } from '../../../services/pusher-service/timeouts';
import { createPusherMessageCache } from '../../pusher/message-cache';

const ALLOWED_RETRIES = 3;

/** @typedef {import('@shipstation/async-client-manager/dist/types.d.ts').FallbackOptions} FallbackOptions */
/** @typedef {import('@shipstation/async-client-manager/dist/types.d.ts').JobGuidManager} JobGuidManager */

/**
 * @param {() => void} clearInitialNotification
 * @param {() => void} showFailureNotification
 * @param {number} documentCount
 * @param {string[]} jobGuids
 * @param {number?} pusherTimeoutMs
 * @returns {{ jobGuidManager: JobGuidManager, fallbackOptions: FallbackOptions }}
 */
const createFallbackOptions = (clearInitialNotification, showFailureNotification, documentCount, jobGuids, pusherTimeoutMs) => {
  const jobGuidManager = jobGuidManagerFactory();
  const fallbackOptions = {
    timeoutMs: pusherTimeoutMs || BULK_PRINT_TIMEOUT_MS,
    jobGuidManager,
    resetTimeoutCheck: () => false,
    /**
     * @param {object} pusherMsg
     * @param {string[]} _jobGuids
     * @returns {boolean}
     */
    completeCheck: (pusherMsg, _jobGuids) => {
      const [jobGuid] = _jobGuids;
      return jobGuid === pusherMsg.messageGuid;
    },
    timeoutCompleteCheck: ({ messages }) => {
      return messages?.length === 1;
    },
    timeoutRetryCheck: ({ retryCount }) => {
      if (retryCount < ALLOWED_RETRIES) {
        return true;
      }
      return false;
    },
    timeoutFailureCheck: ({ messages, retryCount }) => {
      if (!messages?.length && retryCount >= ALLOWED_RETRIES) {
        return true;
      }
      return false;
    },
    /**
     * @param {object} logContext
     */
    onTimeout: (logContext) => {
      clearInitialNotification();
      showFailureNotification();
      LogService.logInfo('Timed out waiting for async print', {
        ...logContext,
        documentCount,
      });
    },
    logContext: {
      jobGuids,
      documentCount,
    },
  };

  return { jobGuidManager, fallbackOptions };
};

/**
 * @param {object} options
 * @param {string[]} options.jobGuids - job guids returned from various async print endpoints
 * @param {number} options.documentCount
 * @param {string} options.initialNotificationId - id from NotificationService.showNotification
 * @param {() => void} options.showFailureNotification
 * @param {number} [options.pusherTimeoutMs] - defaults to BULK_PRINT_TIMEOUT_MS, useful for testing
 * @returns {import('rxjs').Observable<{ downloadUrl: string }>|import('rxjs').Observable<never>}
 */
export const handleBulkPrintPusherMessage = ({
  jobGuids,
  documentCount,
  initialNotificationId,
  showFailureNotification,
  pusherTimeoutMs,
}) => {
  const clearInitialNotification = () => NotificationService.removeNotification(initialNotificationId);

  const { jobGuidManager, fallbackOptions } = createFallbackOptions(
    clearInitialNotification,
    showFailureNotification,
    documentCount,
    jobGuids,
    pusherTimeoutMs,
  );
  jobGuids.forEach((jobGuid) => jobGuidManager.addJobGuid(jobGuid));
  /**
   * @param {object} msg - message from pusher socket
   * @returns {boolean}
   */
  const messageChecker = (msg) => jobGuids.includes(msg.messageGuid);

  const { findMessage } = createPusherMessageCache([BULK_PRINT], fallbackOptions);
  return findMessage(messageChecker).pipe(
    take(1),
    mergeMap((pusherMessage) => {
      const { DownloadUrl, Error } = pusherMessage.data;
      clearInitialNotification();

      if (Error || !DownloadUrl) {
        showFailureNotification();

        return EMPTY;
      }
      return of({ downloadUrl: DownloadUrl });
    }),
    catchError((e) => {
      LogService.logInfo('unexpected error during async print', {
        error: e,
        jobGuids,
      });
      return EMPTY;
    }),
  );
};
